// Which layout the takeoff canvas starts from, by the size of the window it is in. The canvas was laid out for two large
// screens; a Surface Pro (about 1430 x 785 maximized) or a half-width window needs less chrome and a different dock.
// Only DEFAULTS are decided here — a viewer's own choices (pin, dock columns) always win (see ribbonPin / dockLayout).
//
//   wide    >= 1500 wide AND >= 900 tall   the full ribbon (every tile), the saved dock layout — as it always was
//   medium  otherwise, not compact         tabbed ribbon (pinned), the dock in ONE column
//   compact < 1100 wide OR < 850 tall      tabbed ribbon (unpinned: a slim bar + quick tools), dock one column;
//                                          under 1100 wide the dock is a slide-over drawer instead of taking width

import { useEffect, useState } from "react";

export const WIDE_MIN_W = 1500;
export const WIDE_MIN_H = 900;
export const DRAWER_MAX_W = 1100;   // narrower than this the dock slides over the drawing instead of sitting beside it
export const SHORT_MAX_H = 850;     // shorter than this the ribbon starts unpinned and the strips under the drawing shrink

// The window size is not the ribbon's size: the ribbon sits above the drawing, beside the dock, so a large window with the dock open
// (740 px by default in two columns) can leave the ribbon far narrower than the window. Below this MEASURED width the full set of
// tiles wraps into tall rows with empty gaps, so the ribbon is tabbed whatever the window tier says. Width 0 = not measured yet:
// the window tier decides.
export const RIBBON_FULL_MIN_W = 2100;

/** Should the ribbon be tabbed? The window tier says so, or the ribbon's own measured width is too narrow for every tile. */
export const ribbonTabbed = ({ tierTabbed, ribbonWidth }) => !!tierTabbed || (+ribbonWidth > 0 && +ribbonWidth < RIBBON_FULL_MIN_W);

export function layoutTier({ width, height }) {
  const w = +width || WIDE_MIN_W, h = +height || WIDE_MIN_H;
  if (w >= WIDE_MIN_W && h >= WIDE_MIN_H) {
    return { tier: "wide", tabbedRibbon: false, defaultRibbonPinned: true, dockMode: "saved", compact: false };
  }
  const compact = w < DRAWER_MAX_W || h < SHORT_MAX_H;
  return {
    tier: compact ? "compact" : "medium",
    tabbedRibbon: true,
    defaultRibbonPinned: h >= SHORT_MAX_H,
    dockMode: w < DRAWER_MAX_W ? "drawer" : "one",
    compact,
  };
}

export const currentViewport = () => (typeof window === "undefined" ? { width: WIDE_MIN_W, height: WIDE_MIN_H } : { width: window.innerWidth, height: window.innerHeight });

/** The tier of the window right now, following resizes (and browser zoom, which changes innerWidth / innerHeight). */
export function useViewportTier() {
  const [vp, setVp] = useState(currentViewport);
  useEffect(() => {
    let t = null;
    const onResize = () => { clearTimeout(t); t = setTimeout(() => setVp(currentViewport()), 120); };
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); clearTimeout(t); };
  }, []);
  return { ...layoutTier(vp), width: vp.width, height: vp.height };
}
